import {
  AccountBudgetLimitDetails,
  AccountBudgetLimitErrors,
  AccountBudgetLimitValues,
  AccountType,
  Account,
} from '@/types/parley';
import { Ref } from 'vue';
import { AxiosError } from 'axios';

//TODO update when term limit is needed

export const LIMIT_OPTIONS: {
  value: string;
  label: {
    limit: keyof Partial<AccountBudgetLimitValues>;
    threshold: keyof Partial<AccountBudgetLimitValues>;
    label: string;
  };
}[] = [
  {
    value: 'monthly',
    label: {
      limit: 'monthlyBudgetLimit',
      threshold: 'monthlyBudgetLimitAlertThreshold',
      label: 'Monthly',
    },
  },
];

export const FIRST_ACCOUNT_TYPE: AccountType[] = [
  'student',
  'faculty',
  'researcher',
  'staff',
  'postdoc',
];

// Staff and grad students are cost-object-only — mirrors the backend's
// COST_OBJECT_ONLY_AFFILIATIONS (accounts.utils.ts). Everyone else may use
// either billing method. Shared by every billing-method picker (Add
// Account, Edit Account, Add billing method) so the rule can't drift
// between them.
export const COST_OBJECT_ONLY_ACCOUNT_TYPES: AccountType[] = ['staff', 'gradStudent'];

export function canUseStripe(accountType?: AccountType | string | null): boolean {
  return !COST_OBJECT_ONLY_ACCOUNT_TYPES.includes(accountType as AccountType);
}

// Mirror image of canUseStripe: staff/gradStudent can always elect cost
// object; everyone else can only when the student-cost-object-billing flag
// is on (getStudentCostObjectBillingEnabled, parley-api).
export function canElectCostObject(
  accountType: AccountType | string | null | undefined,
  studentCostObjectBillingEnabled: boolean
): boolean {
  return (
    COST_OBJECT_ONLY_ACCOUNT_TYPES.includes(accountType as AccountType) ||
    studentCostObjectBillingEnabled
  );
}

export const budgetLimitValidation = (
  budgetLimits: Partial<AccountBudgetLimitDetails>,
  budgetLimitError: Ref<Partial<AccountBudgetLimitErrors>>
): {
  validated: boolean;
  monthlyBudgetLimit: number;
  monthlyBudgetLimitAlertThreshold: number;
  creditDepletionAlertEnabled: boolean;
  creditDepletionAlertThreshold: number;
} => {
  const keys = Object.keys(budgetLimitError.value) as (keyof AccountBudgetLimitErrors)[];
  keys.forEach((key) => {
    budgetLimitError.value[key] = undefined;
  });

  if (Number(budgetLimits.monthlyBudgetLimit) !== 0) {
    if (Number(budgetLimits.monthlyBudgetLimitAlertThreshold) < 50) {
      budgetLimitError.value.monthlyBudgetLimitAlertThreshold =
        'Budget threshold must be 50% or greater.';
      return {
        validated: false,
        monthlyBudgetLimit: 0,
        monthlyBudgetLimitAlertThreshold: 0,
        creditDepletionAlertThreshold: budgetLimits?.creditDepletionAlertThreshold ?? 0,
        creditDepletionAlertEnabled: !!budgetLimits?.creditDepletionAlertEnabled,
      };
    }

    if (Number(budgetLimits.monthlyBudgetLimitAlertThreshold) > 95) {
      budgetLimitError.value.monthlyBudgetLimitAlertThreshold =
        'Budget threshold must be less than or equal to 95%.';
      return {
        validated: false,
        monthlyBudgetLimit: 0,
        monthlyBudgetLimitAlertThreshold: 0,
        creditDepletionAlertThreshold: budgetLimits?.creditDepletionAlertThreshold ?? 0,
        creditDepletionAlertEnabled: !!budgetLimits?.creditDepletionAlertEnabled,
      };
    }

    if (Number(budgetLimits.monthlyBudgetLimit) < 0) {
      budgetLimitError.value.monthlyBudgetLimit = 'Budget limit must be greater than 0.';
      return {
        validated: false,
        monthlyBudgetLimit: 0,
        monthlyBudgetLimitAlertThreshold: 0,
        creditDepletionAlertThreshold: budgetLimits?.creditDepletionAlertThreshold ?? 0,
        creditDepletionAlertEnabled: !!budgetLimits?.creditDepletionAlertEnabled,
      };
    }

    if (Number(budgetLimits.monthlyBudgetLimit) > 10000) {
      budgetLimitError.value.monthlyBudgetLimit = 'Budget limit must be less than $10,000.';
      return {
        validated: false,
        monthlyBudgetLimit: 0,
        monthlyBudgetLimitAlertThreshold: 0,
        creditDepletionAlertThreshold: budgetLimits?.creditDepletionAlertThreshold ?? 0,
        creditDepletionAlertEnabled: !!budgetLimits?.creditDepletionAlertEnabled,
      };
    }

    if (
      !budgetLimits.monthlyBudgetLimitAlertThreshold ||
      Number(budgetLimits.monthlyBudgetLimitAlertThreshold) === 0
    ) {
      budgetLimitError.value.monthlyBudgetLimitAlertThreshold =
        'Budget threshold is required when a budget limit is set.';
      return {
        validated: false,
        monthlyBudgetLimit: 0,
        monthlyBudgetLimitAlertThreshold: 0,
        creditDepletionAlertThreshold: budgetLimits?.creditDepletionAlertThreshold ?? 0,
        creditDepletionAlertEnabled: !!budgetLimits?.creditDepletionAlertEnabled,
      };
    }
  }

  if (Number(budgetLimits.monthlyBudgetLimitAlertThreshold) !== 0) {
    if (!Number(budgetLimits.monthlyBudgetLimit) || Number(budgetLimits.monthlyBudgetLimit) < 0) {
      budgetLimitError.value.monthlyBudgetLimit =
        'Budget limit is required when a budget threshold is set.';
      return {
        validated: false,
        monthlyBudgetLimit: 0,
        monthlyBudgetLimitAlertThreshold: 0,
        creditDepletionAlertThreshold: budgetLimits?.creditDepletionAlertThreshold ?? 0,
        creditDepletionAlertEnabled: !!budgetLimits?.creditDepletionAlertEnabled,
      };
    }
  }

  if (budgetLimits?.creditDepletionAlertEnabled) {
    if (Number(budgetLimits.creditDepletionAlertThreshold) < 50) {
      budgetLimitError.value.creditDepletionAlertThreshold =
        'Budget threshold must be 50% or greater.';
      return {
        validated: false,
        creditDepletionAlertThreshold: budgetLimits?.creditDepletionAlertThreshold ?? 0,
        creditDepletionAlertEnabled: !!budgetLimits?.creditDepletionAlertEnabled,
        monthlyBudgetLimit: budgetLimits?.monthlyBudgetLimit ?? 0,
        monthlyBudgetLimitAlertThreshold: budgetLimits?.monthlyBudgetLimitAlertThreshold ?? 0,
      };
    }

    if (Number(budgetLimits.creditDepletionAlertThreshold) > 95) {
      budgetLimitError.value.creditDepletionAlertThreshold =
        'Budget threshold must be less than or equal to 95%.';
      return {
        validated: false,
        creditDepletionAlertThreshold: budgetLimits?.creditDepletionAlertThreshold ?? 0,
        creditDepletionAlertEnabled: !!budgetLimits?.creditDepletionAlertEnabled,
        monthlyBudgetLimit: budgetLimits?.monthlyBudgetLimit ?? 0,
        monthlyBudgetLimitAlertThreshold: budgetLimits?.monthlyBudgetLimitAlertThreshold ?? 0,
      };
    }
  }

  return {
    validated: true,
    monthlyBudgetLimit: Number(budgetLimits.monthlyBudgetLimit ?? 0),
    monthlyBudgetLimitAlertThreshold: Number(budgetLimits.monthlyBudgetLimitAlertThreshold ?? 0),
    creditDepletionAlertEnabled: !!budgetLimits?.creditDepletionAlertEnabled,
    creditDepletionAlertThreshold: budgetLimits?.creditDepletionAlertThreshold ?? 0,
  };
};

export const apiKeyBudgetLimitValidationAgainstAccount = (
  accountLimits: Partial<AccountBudgetLimitDetails>,
  keyLimits: Partial<AccountBudgetLimitDetails>,
  keyLimitError: Ref<Partial<AccountBudgetLimitErrors>>,
  account: Account | null
) => {
  keyLimitError.value.monthlyBudgetLimit = undefined;

  if (!!accountLimits?.monthlyBudgetLimit && accountLimits?.monthlyBudgetLimit > 0) {
    if (
      keyLimits.monthlyBudgetLimit &&
      accountLimits?.monthlyBudgetLimit < keyLimits.monthlyBudgetLimit
    ) {
      keyLimitError.value.monthlyBudgetLimit =
        'API key budget limit cannot exceed account budget limit.';
      return {
        keyValidated: false,
        keyLimits,
        keyLimitError,
      };
    }

    if (
      (keyLimits.monthlyBudgetLimit === 0 || !keyLimits.monthlyBudgetLimit) &&
      accountLimits?.monthlyBudgetLimit > 0
    ) {
      keyLimitError.value.monthlyBudgetLimit =
        'API key budget limit cannot be 0 if the account has a budget limit.';
      return {
        keyValidated: false,
        keyLimits,
        keyLimitError,
      };
    }
  }

  if (
    (!accountLimits?.monthlyBudgetLimit ||
      (!!accountLimits?.monthlyBudgetLimit && accountLimits.monthlyBudgetLimit <= 0)) &&
    account?.billingMethod !== 'cost_object' &&
    account?.billingMethod !== 'stripe' &&
    account?.fundingSource !== 'cost_object' &&
    account?.fundingSource !== 'stripe'
  ) {
    // The allocation-amount cap only makes sense for accounts that actually
    // have an allocation assigned (account.allocationId). An account with no
    // account-level budget limit and no cost_object/stripe billing is NOT
    // necessarily allocation-funded (e.g. a secondary, non-personal account)
    // — such accounts must only be capped by the platform-wide limit
    // (enforced separately by budgetLimitValidation), not by the $30 default
    // subsidy fallback meant for allocation-funded accounts.
    if (
      !!account?.allocationId &&
      !!keyLimits.monthlyBudgetLimit &&
      keyLimits.monthlyBudgetLimit > (account?.allocation?.amount ?? 30)
    ) {
      keyLimitError.value.monthlyBudgetLimit =
        'API key budget limit cannot exceed account allocation limit.';
      return {
        keyValidated: false,
        keyLimits,
        keyLimitError,
      };
    }

    // Same reasoning as above: 0 means "unlimited" everywhere else in this
    // codebase (account-level budgets, and the backend's per-key validator
    // explicitly retains 0 to mean "no limit"). Refusing 0 only makes sense
    // when the key draws from a shared allocation pool — an unlimited key
    // would let it consume the whole pool. A non-allocation account has no
    // such pool to protect, so 0 is a legitimate cap there too.
    if (
      !!account?.allocationId &&
      (keyLimits.monthlyBudgetLimit === 0 || !keyLimits.monthlyBudgetLimit)
    ) {
      keyLimitError.value.monthlyBudgetLimit =
        'API key budget limit cannot be 0 if the account has a monthly allocation.';
      return {
        keyValidated: false,
        keyLimits,
        keyLimitError,
      };
    }
  }

  return {
    keyValidated: true,
    keyLimits,
    keyLimitError,
  };
};

export const extractErrorMessage = (e: unknown, fallback: string): string => {
  if (e instanceof AxiosError && e.response?.data) {
    const data = e.response.data as Record<string, unknown>;
    if (typeof data.message === 'string') return data.message;
    if (data.error && typeof (data.error as Record<string, unknown>).message === 'string') {
      return (data.error as Record<string, unknown>).message as string;
    }
  }
  if (e instanceof Error) return e.message;
  return fallback;
};
